import { Css, useTestIds } from "@homebound/beam";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Price } from "src/components";
import {
  TaskDetailsPage_PlanTaskFragment,
  TaskPurchaseOrderRowDisplayFragment,
} from "src/fragments/circularFragments.generated";
import { createCommitmentChangeOrderUrl, createCommitmentUrl } from "src/RouteUrls";

type PurchaseOrdersProps = {
  inSidePane?: boolean;
  projectId: string;
  taskPurchaseOrders: TaskDetailsPage_PlanTaskFragment["taskPurchaseOrders"];
};

type PurchaseOrderRowProps = { projectId: string; tid: ReturnType<typeof useTestIds> } & (
  | { kind: "po"; row: TaskPurchaseOrderRowDisplayFragment }
  | { kind: "co"; commitmentId: string; row: TaskPurchaseOrderRowDisplayFragment }
);

export function PurchaseOrders(props: PurchaseOrdersProps) {
  const { inSidePane = false, projectId, taskPurchaseOrders } = props;
  const tid = useTestIds({}, "purchaseOrders");
  const totals = useMemo(() => {
    const rows = taskPurchaseOrders.flatMap((row) => [
      row,
      ...row.changeOrders.map(({ committedInCents, unbilledInCents }) => ({ committedInCents, unbilledInCents })),
    ]);
    return {
      committedInCents: rows.sum((row) => row.committedInCents),
      unbilledInCents: rows.sum((row) => row.unbilledInCents),
    };
  }, [taskPurchaseOrders]);

  return (
    <div css={Css.w100.bcGray300.if(!inSidePane).bb.pb3.$}>
      <div css={Css.if(!inSidePane).my2.else.mb2.$}>
        <span css={Css.df.aic.jcsb.mdSb.gray800.$}>Purchase Orders</span>
      </div>
      {taskPurchaseOrders.nonEmpty ? (
        <div css={Css.df.fdc.gap1.mb1.$}>
          <div css={Css.dg.gtc("100px 80px 1fr 64px 80px").w100.cg1.rg1.xs2.gray700.aic.mb1.$}>
            <span>PO#</span>
            <span>Trade</span>
            <div css={Css.maxwPx(120).tal.$}>Status</div>
            <div css={Css.tal.$} {...tid.committedHeader}>
              Committed
            </div>
            <div css={Css.tal.pl1.$} {...tid.unbilledHeader}>
              Unbilled
            </div>
          </div>
          {taskPurchaseOrders.map((row) => (
            <div css={Css.df.fdc.w100.mb1.br8.$} key={row.commitmentLike.id}>
              <PurchaseOrderRow kind="po" projectId={projectId} tid={tid} row={row} />
              {row.changeOrders.map((changeOrderRow) => (
                <PurchaseOrderRow
                  key={changeOrderRow.commitmentLike.id}
                  kind="co"
                  projectId={projectId}
                  tid={tid}
                  commitmentId={row.commitmentLike.id}
                  row={changeOrderRow}
                />
              ))}
            </div>
          ))}
          <div css={Css.dg.gtc("100px 80px 1fr 64px 80px").w100.cg1.rg1.sm.aic.pt1.bt.bcGray200.$}>
            <span css={Css.gray700.$} {...tid.total}>
              Total
            </span>
            <span />
            <span />
            <div css={Css.smSb.tal.$} {...tid.committedTotal}>
              <Price valueInCents={totals.committedInCents} />
            </div>
            <div css={Css.smSb.tal.pl1.$} {...tid.unbilledTotal}>
              <Price valueInCents={totals.unbilledInCents} />
            </div>
          </div>
        </div>
      ) : (
        <div {...tid.noPurchaseOrders} css={Css.br8.bsDashed.bcGray200.gray700.bw("3px").py2.df.jcc.$}>
          There are no linked POs
        </div>
      )}
    </div>
  );
}

function PurchaseOrderRow(props: PurchaseOrderRowProps) {
  const { kind, projectId, tid } = props;
  const { committedInCents, unbilledInCents } = props.row;

  const link =
    kind === "po" ? (
      <Link to={createCommitmentUrl(projectId, props.row.commitmentLike.id)}>
        PO#{props.row.commitmentLike.accountingNumber}
      </Link>
    ) : (
      <Link to={createCommitmentChangeOrderUrl(projectId, props.commitmentId, props.row.commitmentLike.id)}>
        CO#{props.row.commitmentLike.accountingNumber}
      </Link>
    );

  const tradePartnerName = props.row.commitmentLike.tradePartner?.name;
  const statusText = props.row.commitmentLike.statusText;

  return (
    <div css={Css.dg.gtc("100px 80px 1fr 64px 80px").w100.cg1.rg1.smSb.aic.if(kind === "co").mt1.$}>
      <span {...tid.accountNumber}>{kind === "co" ? <span css={Css.pl2.$}>{link}</span> : link}</span>
      <span css={Css.sm.$} {...tid.tradePartner}>
        {tradePartnerName}
      </span>
      <div css={Css.maxwPx(120).tal.$}>
        <span css={Css.sm.$}>{statusText}</span>
      </div>
      <div css={Css.sm.tal.$} {...tid.committed}>
        <Price valueInCents={committedInCents} />
      </div>
      <div css={Css.sm.tal.pl1.$} {...tid.unbilled}>
        <Price valueInCents={unbilledInCents} />
      </div>
    </div>
  );
}
